/**
 * Public POST /estimates/estimate contracts (OM-372 Estimate Manager).
 * Trusted geo/shipDate/products are derived server-side from verified JWTs.
 */

/** Physical package piece from the Package step. */
export interface EstimateManagerPackageItem {
  identicalPieces: number;
  perPieceWeight: number;
  length: number;
  width: number;
  height: number;
  /** User-selected handling intent used by the backend to derive SH06. */
  requiresAdditionalHandling?: boolean;
}

/** Coupon payload for a selected-product discount request. */
export interface OrchestratorCoupon {
  couponCode: string;
  email: string;
}

/**
 * Public POST /estimates/estimate body.
 * Do not send origin/destination/shipDate/products — those come from attestation JWTs.
 */
export interface EstimateManagerRequest {
  productSelectionTokens: string[];
  packageSelectionToken: string;
  serviceAbilityToken: string;
  /** Package-step JWT_psa required for agent weight filtering (OM-872). */
  serviceAvailabilityToken: string;
  accountLookupToken: string;
  packages?: EstimateManagerPackageItem[];
  /** Required when packages is non-empty; maps from Package flow state (LB or KG). */
  shipmentWeightUom?: 'LB' | 'KG';
  /** Required when packages is non-empty; maps from Package flow state (IN or CM). */
  shipmentDimensionUom?: 'IN' | 'CM';
  /** When true, backend maps to RES. Mutually exclusive with hasAdultSignature. */
  hasResidentialSignature?: boolean;
  /**
   * When true, backend maps to ZS50. Mutually exclusive with hasResidentialSignature.
   * When both signature flags are omitted, backend maps to OSNR.
   */
  hasAdultSignature?: boolean;
  /** Declared value amount; when missing, null, or ≤0, backend omits COV. */
  declaredValue?: number | null;
}

/**
 * Public POST /estimates/discount body for re-rating one selected product with a coupon.
 * JWT_est is the trusted source; productCode and expectedDeliveryDate only select
 * the Product × EDD result inside it, so no product-selection token is sent.
 */
export interface DiscountEstimateRequest {
  /** Prior standard-estimate JWT_est containing trusted shipment and rate context. */
  estimateToken: string;
  /** Product code of the service selected by the customer. */
  productCode: string;
  /** Expected delivery date identifying the selected Product × EDD result. */
  expectedDeliveryDate: string;
  coupon: OrchestratorCoupon;
}

/** @deprecated Use {@link EstimateManagerRequest}. */
export type OrchestrateRequest = EstimateManagerRequest;

/** Warning item on the Estimate Manager response. */
export interface OrchestratorWarning {
  code: string;
  message: string;
  productCode?: string;
  expectedDeliveryDate?: string;
  productCodes?: string;
  shipmentDate?: string;
}

/** String pricing block nested under each Product × EDD estimate result. */
export interface EstimateManagerPricing {
  basePrice: string;
  totalPrice: string;
  fuelSurcharge: string;
  taxes: {
    GST: string;
    PST: string;
    HST: string;
    QST: string;
  };
  surcharges: {
    peak: string;
    multiPiece: string;
    holiday: string;
  };
  /** Per-SH peak breakdown from nested S4 PeakSurchargeAmount (OM-724). */
  peakSurcharges?: {
    productCode: string;
    amount: string;
  }[];
  accessorialCharges: {
    productCode: string;
    amount: string;
  }[];
}

/** Estimate outcome + pricing nested under each Product × EDD result. */
export interface EstimateManagerEstimateResult {
  outcome: string;
  pricing: EstimateManagerPricing | null;
  discountedPricing?: EstimateManagerPricing | null;
  error: { description: string; details?: { key?: string; value?: string }[] } | null;
}

/** Service Availability projection nested under each Product × EDD result. */
export interface EstimateManagerServiceAvailability {
  shipmentDate: string;
  expectedDeliveryDate: string;
  productCode: string;
  transportationMode: string;
  productNameEn?: string;
  productNameFr?: string;
  productType?: string;
  serviceTimeCode?: string;
  totalTransitDays?: number;
}

/** Coupon projection nested under each Product × EDD result. */
export type EstimateManagerCouponStatus =
  'NOT_SUPPLIED' | 'VALID' | 'INVALID' | 'VALIDATION_UNAVAILABLE';

export interface EstimateManagerCoupon {
  status: EstimateManagerCouponStatus;
  couponCode: string | null;
  /** Legacy string percent when present; prefer `discountRate` from discount responses. */
  contractValue?: string | null;
  /** Authoritative coupon percent from estimate discount / validate. */
  discountRate?: number | null;
  discountType?: string | null;
}

/** Catalog product facts nested under each Product × EDD result. */
export interface EstimateManagerProductFacts {
  productCode: string;
  descEn?: string;
  descFr?: string;
  serviceTimeCode?: string;
  serviceTimeEnglish?: string;
  serviceTimeFrench?: string;
  productTypeCode?: string;
  productTypeEnglish?: string;
  productTypeFrench?: string;
  [key: string]: unknown;
}

/** Selected rate and zone metadata nested under each Product × EDD result. */
export interface EstimateManagerRateCode {
  rateCode: string;
  originZoneCode?: string;
  destinationZoneCode?: string;
  context: {
    courier: Record<string, unknown>;
    freight?: unknown;
  };
}

/** Rated package row echoed in `ratingInput.items`, with server-derived SH service codes. */
export interface EstimateManagerRatedItem {
  identicalPieces?: number;
  services?: { serviceCode?: string }[];
  [key: string]: unknown;
}

/** Canonical rating input echoed per product; only the fields the UI reads are typed. */
export interface EstimateManagerRatingInput {
  items?: EstimateManagerRatedItem[];
  [key: string]: unknown;
}

/**
 * Complete projection for one Product × expected delivery date.
 * No nested attestation — only the top-level manager response is signed.
 */
export interface EstimateManagerProductResponse {
  product: EstimateManagerProductFacts;
  estimate: EstimateManagerEstimateResult;
  serviceAvailability: EstimateManagerServiceAvailability;
  rateCode?: EstimateManagerRateCode;
  ratingInput?: EstimateManagerRatingInput;
  coupon: EstimateManagerCoupon;
}

/** One expected-delivery-date bucket in the Estimate Manager response. */
export interface EstimateManagerDeliveryDateOption {
  shipmentDate: string;
  expectedDeliveryDate: string;
  products: EstimateManagerProductResponse[];
}

/** POST /estimates/estimate success body. */
export interface EstimateManagerResponse {
  currency: string;
  rateCode: string;
  originZoneCode?: string;
  destinationZoneCode?: string;
  warnings: OrchestratorWarning[];
  availableDeliveryDates: EstimateManagerDeliveryDateOption[];
  /** Single top-level JWT_est (`type: estimate`). */
  attestation: string;
}

/** @deprecated Use {@link EstimateManagerResponse}. */
export type OrchestrateResponse = EstimateManagerResponse;

/** Warning code when coupon validation fails but priced options are still returned. */
export const COUPON_VALIDATION_FAILED = 'COUPON_VALIDATION_FAILED';
